/*
 * Copyright (c) 2024-2025. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import React, {useEffect, useRef, useState} from "react";
import {Timeslot} from "types/entities/Timeslot";
import {useTranslation} from "react-i18next";
import {useTypedSelector} from "helpers/reducers";
import {RouteComponentProps, withRouter} from "react-router-dom";
import Dates from "utils/Dates";
import {ClockIcon} from "@heroicons/react/24/outline";
import clsx from "clsx";
import Loader from "components/Loader";
import {PackageCategory} from "types/enums/PackageCategory";

import {fakeTimeslotMinute, getPackageRespectsTimeslotSize, useClearUpsells} from "utils/packageUtils";
import BookingTimeslot from "./components/BookingTimeslots/BookingTimeslot";
import AvailableDateButton from "./components/BookingTimeslots/AvailableDateButton";
import useStepsHelper from "utils/hooks/useStepsHelper";
import useCustomTimeslots from "utils/hooks/useCustomTimeslots";
import {BookingSteps} from "types/enums/BookingSteps";
import useNavigation from "utils/hooks/useNavigation";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import {HallCode} from "types/enums/HallCode";
import ImportantArriveTime from "pages/Front/Booking/components/ImportantArriveTime";
import NeedHelpWithTimeslots from "pages/Front/Booking/components/BookingTimeslots/NeedHelpWithTimeslots";
import {DEFAULT_HALL, ENABLE_HELP_WITH_TIMESLOTS, LOCALE, SELECT_CUSTOM_TIMESLOTS_ALLOWED} from "helpers/config";
import {useReactGoogleAnalytics} from "utils/useReactGoogleAnalytics";

export interface BookingTimeslotParams {
    package_id: string;
}

interface Props extends RouteComponentProps<BookingTimeslotParams> {
    selectTimeslotHandler: (bookingStep: BookingSteps) => void;
}

const BookingTimeslots = (props: Props) => {
    const {t} = useTranslation();
    const {restartProcess} = useNavigation();
    const bookingTimeslots = useTypedSelector(state => state.bookingState.timeslots);
    const sessionLangauge = useTypedSelector(state => state.sessionState.language);
    const bookingProcess = useTypedSelector(state => state.bookingState.bookingProcess);
    const filter = useTypedSelector(state => state.bookingState.bookingProcess.filter);
    const packagesCart = useTypedSelector(state => state.bookingState.bookingProcess.packageCart);
    const selectedPackage = useTypedSelector(state => state.bookingState.bookingProcess.package);
    const customSelectedTimeslotState = useTypedSelector(state => state.bookingState.bookingProcess.customTimeslots);
    const [lastBookingTimeslotSize, setLastBookingTimeslotSize] = useState(16);
    const [skeletonArray, setSkeletonArray] = useState<any[]>([]);
    //the timeslot that is selected in the cart or by automatic selection
    const packageRespectsTimeslotSize = selectedPackage && getPackageRespectsTimeslotSize(selectedPackage);

    const lastMidnight = new Date();
    lastMidnight.setHours(0, 0, 0, 0);

    const selectedUpsells = useTypedSelector(state => state.bookingState.bookingProcess.upsell);
    const {sendGenericEvent} = useReactGoogleAnalytics();

    const clearPackagesHook = useClearUpsells(bookingProcess);

    const scrollRefTimeslots = useRef<HTMLDivElement>(null);
    const scrollRefCustomTimeslots = useRef<HTMLDivElement>(null);
    const scrollRefConfirmTimeslots = useRef<HTMLDivElement>(null);
    const blockedClass = "bg-gray-600 pointer-events-none opacity-50";

    const {
        availableDates,
        bookWithAutomaticTimeslot,
        bookWithCustomTimeslot,
        currentDateIndex,
        customSelectedTimeslots,
        firstPossibleSlotIndex,
        isFetchingTimeslot,
        isLoadingTimeslots,
        lastPossibleSlotIndex,
        possibleTimeslots,
        resetCustomSelectedTimeslotHandler,
        selectCustomSlotsEnabled,
        selectedTimeslot,
        setSelectCustomSlotsEnabledHandler,
        setSelectedTimeslotHandler,
        setStepsEnabledHandler,
        stepsEnabled,
        switchDate,
        timeslotsAvailable,
    } = useCustomTimeslots(props.selectTimeslotHandler, props.history, props.match, [scrollRefTimeslots, scrollRefCustomTimeslots, scrollRefConfirmTimeslots]);

    useEffect(() => {
        clearPackagesHook.clearInvalidUpsells();
    }, [selectedUpsells]);

    useEffect(() => {
        if (!selectedPackage) {
            restartProcess();
        }
    }, [selectedPackage]);

    const stepsHandler = useStepsHelper(selectedPackage, stepsEnabled, setStepsEnabledHandler, setSelectedTimeslotHandler, resetCustomSelectedTimeslotHandler);

    useEffect(() => {
        setSkeletonArray(new Array(lastBookingTimeslotSize).fill(""));
    }, [lastBookingTimeslotSize]);

    useEffect(() => {
        setLastBookingTimeslotSize(timeslotsAvailable.length);
    }, [timeslotsAvailable]);

    if (!packagesCart || !bookingTimeslots || !selectedPackage) {
        return <Loader />;
    }
    return (
        <div
            className={"BookingTimeslots max-w-5xl m-auto flex flex-col w-full h-full flex-grow justify-between items-center text-center select-none relative"}
        >
            {timeslotsAvailable && timeslotsAvailable.length > 0 && stepsEnabled && <stepsHandler.StepsComponent stepsEnabled={stepsEnabled} />}

            <div
                className={
                    "BookingTimeslotHeader grid grid-cols-3 sticky top-0 z-40 bg-gray-100 justify-between mb-4 -mx-2 lg:mb-4 lg:-m-2 w-full max-w-3xl px-4 lg:p-2"
                }
            >
                <div className={"BookingTimeslotHeader col-span-3 flex flex-col items-center justify-center w-full mt-4"}>
                    <h3 className={"text-lg leading-5 sm:text-3xl font-bold items-center"}>
                        <span>{DEFAULT_HALL === HallCode.ArboPark ? t("Choose timeslot") : t("Choose timeslots")}</span>
                    </h3>
                    {selectCustomSlotsEnabled && customSelectedTimeslots.length >= 0 && (
                        <div className={"flex flex-row"}>
                            <span>{customSelectedTimeslots.length}</span>
                            <span>/</span>
                            <span>{selectedPackage?.typical_timeslot_count}</span>
                        </div>
                    )}
                </div>
                <AvailableDateButton
                    availableDates={availableDates}
                    currentDateIndex={currentDateIndex}
                    direction={"prev"}
                    onClick={date => switchDate(date)}
                />
                <div className={"relative flex flex-col items-center justify-end w-full text-base 2xl:text-lg py-2"}>
                    <span className={"font-bold"}>{Dates.intlDateFormat(new Date(filter.date).getTime(), {dateStyle: "short"})}</span>
                    <span className={"text-sm top-8 absolute"}>
                        {Dates.intlDateFormat(new Date(filter.date).getTime(), {weekday: "long"}, sessionLangauge ? sessionLangauge.locale : LOCALE)}&nbsp;
                    </span>
                </div>
                <AvailableDateButton
                    availableDates={availableDates}
                    currentDateIndex={currentDateIndex}
                    direction={"next"}
                    onClick={date => switchDate(date)}
                />
            </div>

            <div className={"relative grow justify-start flex flex-col w-full overflow-y-auto"}>
                <div
                    ref={scrollRefTimeslots}
                    className={
                        "timeslots relative grid w-full grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 justify-center items-center gap-4 py-2 px-1 max-w-3xl mx-auto"
                    }
                    id="BookingTimeslots"
                >
                    {isLoadingTimeslots &&
                        skeletonArray.map((i, index) => {
                            return (
                                <div
                                    key={index}
                                    className={
                                        "w-full min-w-[88px] h-[50px] sm:h-[62px] border border-solid border-gray-800/20  overflow-hidden rounded-lg flex leading-[1]"
                                    }
                                >
                                    <Skeleton containerClassName={"flex-1"} height={"100%"} width={"100%"} />
                                </div>
                            );
                        })}
                    {!isLoadingTimeslots &&
                        timeslotsAvailable.map((timeslot: Timeslot, index: number) => {
                            const possibleTimeslotIndex = possibleTimeslots.findIndex(possible => possible.id === timeslot.id);
                            const customTimeslotIndex = customSelectedTimeslots
                                .sort((a, b) => (a.id > b.id ? 1 : -1))
                                .findIndex(customTimeslot => customTimeslot.id === timeslot.id);
                            return (
                                <BookingTimeslot
                                    key={index}
                                    customTimeslotIndex={customTimeslotIndex}
                                    fakeTimeslotMinute={fakeTimeslotMinute(selectedPackage)}
                                    firstPossibleSlotIndex={firstPossibleSlotIndex}
                                    index={index}
                                    lastPossibleSlotIndex={lastPossibleSlotIndex}
                                    packageRespectsTimeslotSize={packageRespectsTimeslotSize}
                                    possibleTimeslotIndex={possibleTimeslotIndex}
                                    possibleTimeslotsLength={possibleTimeslots.length}
                                    selectedTimeslot={selectedTimeslot}
                                    showTimeslotOccupancy={false}
                                    timeslot={timeslot}
                                    onClick={() => setSelectedTimeslotHandler(timeslot, selectCustomSlotsEnabled)}
                                    showEventHoursCategory={false}
                                />
                            );
                        })}
                    {!isLoadingTimeslots && timeslotsAvailable.length === 0 && (
                        <div className={"w-full flex flex-col items-center justify-center col-span-3 sm:col-span-4 md:col-span-6"}>
                            <div className={"p-3 xl:p-4 rounded-md shadow-md bg-white text-cta-800 max-w-[326px] lg:max-w-[640px]"}>
                                <h3 className={"text-lg font-medium"}>
                                    {t("Sorry")}, {t("completely booked out on this day")}
                                </h3>
                                <span>{t("Please choose another date")}</span>
                            </div>
                        </div>
                    )}
                </div>
            </div>
            <div className={"flex flex-col justify-center items-center h-auto mt-0 sticky inset-x-0 w-full bottom-0 bg-gray-100 z-40 max-w-3xl"}>
                {ENABLE_HELP_WITH_TIMESLOTS && timeslotsAvailable && timeslotsAvailable.length > 0 && (
                    <>
                        <ImportantArriveTime selectedUpsells={selectedUpsells} />
                        <NeedHelpWithTimeslots onClick={() => setStepsEnabledHandler(true)} />
                    </>
                )}
                <div className={"flex flex-row justify-center items-center space-x-2"}>
                    {SELECT_CUSTOM_TIMESLOTS_ALLOWED &&
                        selectedPackage &&
                        selectedPackage.typical_timeslot_count > 1 &&
                        selectedPackage.category !== PackageCategory.Event && (
                            <div className={"SelectCustomTimeslots grid justify-center items-center gap-4 sm:bottom-auto z-40"}>
                                <div ref={scrollRefCustomTimeslots} className={"flex flex-row p-3 sm:p-4 w-full inset-x-0"}>
                                    <button
                                        className={clsx(
                                            selectedTimeslot === null && customSelectedTimeslots.length === 0 && blockedClass,
                                            "bg-primary-800 text-primary-100 cursor-pointer hover:no-underline text-xs sm:text-xl flex-grow text-center px-3 py-2 sm:px-5 sm:py-3 flex flex-row justify-center rounded-lg items-center"
                                        )}
                                        disabled={selectedTimeslot === null && customSelectedTimeslots.length === 0}
                                        type={"button"}
                                        onClick={() => setSelectCustomSlotsEnabledHandler(!selectCustomSlotsEnabled)}
                                    >
                                        <div className={"flex flex-row items-center space-x-1 sm:space-x-2"}>
                                            <ClockIcon className={"w-3 h-3 shrink-0"} />
                                            <span className={"leading-4 sm:leading-7"}>
                                                {selectCustomSlotsEnabled ? t("Dont use my slots") : t("Select my next slots")}
                                            </span>
                                        </div>
                                    </button>
                                </div>
                            </div>
                        )}
                    <div className={"ConfirmTimeslotButton grid justify-center items-center gap-4 sm:bottom-auto z-40"}>
                        <div ref={scrollRefConfirmTimeslots} className={"flex flex-row p-3 w-full inset-x-0"}>
                            <button
                                className={clsx(
                                    filter.children === 0 && filter.adults === 0 && blockedClass,
                                    !selectedTimeslot && !selectCustomSlotsEnabled && blockedClass,
                                    selectedPackage &&
                                        selectCustomSlotsEnabled &&
                                        customSelectedTimeslotState.length !== selectedPackage.typical_timeslot_count &&
                                        blockedClass,
                                    "bg-cta-800 text-contrast-800 cursor-pointer font-medium hover:no-underline text-lg h-14 xl:h-16 sm:text-xl flex-grow text-center px-3 py-2 sm:px-5 sm:py-3 flex flex-row justify-center rounded-lg items-center"
                                )}
                                type={"button"}
                                onClick={
                                    !isFetchingTimeslot && selectedTimeslot
                                        ? () => bookWithAutomaticTimeslot(selectedTimeslot)
                                        : selectCustomSlotsEnabled
                                          ? () => bookWithCustomTimeslot()
                                          : () => false
                                }
                            >
                                <div className={"flex flex-row items-center space-x-1 sm:space-x-2"}>
                                    <ClockIcon className={"w-3 h-3 shrink-0"} />
                                    <span className={"leading-4 sm:leading-7"}>{t("Continue with selected time")}</span>
                                </div>
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default withRouter(BookingTimeslots);
